import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, keys, type Channel, type Uuid } from "@shared/api";
import { IconEvents } from "@shared/ui/icons";
import { endsAt, isLive } from "../lib/when";
import { EventsDialog } from "./EventsDialog";

/**
 * Строка «События» над каналами — как в Discord: сколько впереди, а если что-то
 * идёт прямо сейчас — его имя. Тем, кто событий не заводит и не ждёт, строка не
 * нужна: пустой список без права заводить её прячет.
 */
export function EventsEntry({
  serverId,
  channels,
  meId,
  canCreate,
  canManageAll,
  onJoin,
}: {
  serverId: Uuid;
  channels: Channel[];
  meId: Uuid;
  /** Заводить события — правом «создавать события» или «управлять событиями». */
  canCreate: boolean;
  /** Отменять любые; свои — и с одним правом «создавать». */
  canManageAll: boolean;
  onJoin: (channel: Channel) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data: listed = [] } = useQuery({
    queryKey: keys.events(serverId),
    queryFn: async () => (await api.events.list(serverId)).items,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  /*
   * «Идёт сейчас» и уход прошедшего — по часам, а не по следующей подгрузке:
   * будильник на ближайшее начало или конец перерисовывает строку ровно тогда.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Часы — свежие, а не из состояния: список мог смениться спустя часы после
    // прошлого будильника, и срок от старого «сейчас» сработал бы с опозданием.
    const current = Date.now();
    if (current - now > 1000) {
      setNow(current);
      return;
    }
    const next = Math.min(
      ...listed.flatMap((event) => [Date.parse(event.starts), endsAt(event)]).filter((at) => at > current),
    );
    if (!Number.isFinite(next)) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(next - current + 250, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [listed, now]);

  const events = listed.filter((event) => endsAt(event) > now);
  if (events.length === 0 && !canCreate) return null;
  const live = events.find((event) => isLive(event, now));

  return (
    <>
      {/* Строка канала по виду: своей кнопкой она брала общий стиль стеклянной кнопки. */}
      <button type="button" className={`chan events-entry${live ? " live" : ""}`} onClick={() => setOpen(true)}>
        <IconEvents size={16} />
        <span className="ellipsis grow">
          {live ? `Сейчас: ${live.name}` : events.length > 0 ? `События · ${events.length}` : "События"}
        </span>
      </button>
      {open && (
        <EventsDialog
          serverId={serverId}
          events={events}
          channels={channels}
          meId={meId}
          canCreate={canCreate}
          canManageAll={canManageAll}
          onJoin={(channel) => {
            setOpen(false);
            onJoin(channel);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
